import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PrismAsyncLight as SyntaxHighlighter } from "react-syntax-highlighter"; // Using PrismAsyncLight for smaller bundle
import okaidia from "react-syntax-highlighter/dist/esm/styles/prism/okaidia"; // Importing okaidia style directly
import "./FlashCard.css";
import { lang } from "../../lang";

// Difficulty levels and their labels
const difficultyLevelsMap = {
  easy: lang.flashCards.difficultyLevels.easy || "Easy",
  moderate: lang.flashCards.difficultyLevels.moderate || "Moderate",
  hard: lang.flashCards.difficultyLevels.hard || "Hard",
  mildPanic: lang.flashCards.difficultyLevels.mildPanic || "Mild Panic",
  gotcha: lang.flashCards.difficultyLevels.gotcha || "Gotcha!",
  null: lang.flashCards.difficultyLevels.none || "None",
};

function FlashCard({ card, onUpdateCardDifficulty, onEditCard }) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState(
    card.difficulty === undefined ? null : card.difficulty
  );

  useEffect(() => {
    setIsFlipped(false);
    setSelectedDifficulty(
      card.difficulty === undefined ? null : card.difficulty
    );
  }, [card.id, card.difficulty]);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleDifficultyChange = (newDifficulty) => {
    setSelectedDifficulty(newDifficulty);
    if (onUpdateCardDifficulty) {
      onUpdateCardDifficulty(card.id, newDifficulty);
    }
  };

  const renderContent = (content) => {
    if (typeof content === "string" && content.includes("```")) {
      return (
        <ReactMarkdown
          children={content}
          remarkPlugins={[remarkGfm]}
          components={{
            code({ node, inline, className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || "");
              return !inline && match ? (
                <SyntaxHighlighter
                  children={String(children).replace(/\n$/, "")}
                  style={okaidia} // Use the imported okaidia style
                  language={match[1]}
                  PreTag="div"
                  {...props}
                />
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            },
          }}
        />
      );
    }
    return <div className="card-text-content">{content}</div>;
  };

  return (
    <div
      className={`flashcard-container ${isFlipped ? "flipped" : ""}`}
      onClick={handleFlip}
    >
      <div className="flashcard-inner">
        <div className="flashcard-front">
          {renderContent(card.frontContent)}
        </div>
        <div className="flashcard-back">
          {renderContent(card.backContent)}
          <div className="difficulty-selector-radio">
            <div className="difficulty-label">
              {lang.flashCards.difficultyLabel || "Difficulty:"}
            </div>
            <div className="difficulty-options">
              {Object.entries(difficultyLevelsMap).map(([key, label]) => {
                const valueForRadio = key === "null" ? null : key;
                return (
                  <label key={key} className="difficulty-radio-item-card">
                    <input
                      type="radio"
                      name={`difficulty-${card.id}`}
                      value={key}
                      checked={selectedDifficulty === valueForRadio}
                      onChange={() => handleDifficultyChange(valueForRadio)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          </div>
          {onEditCard && (
            <button
              className="edit-card-button"
              onClick={(e) => {
                e.stopPropagation();
                onEditCard(card);
              }}
            >
              Edit Card
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default FlashCard;
